//cspell:ignore mensaje
import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { WhatsAppTemplate } from '@/src/infrastructure/Protocol/Inbox/IInboxService';
import { useInboxService } from '@/src/infrastructure/Protocol/Inbox/useInboxService';
import { IWhatsAppFormInfo } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/components/WhatsAppForm/hooks/useWhatsAppForm';
import { useWhatsAppFormEvents } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/components/WhatsAppForm/hooks/useWhatsAppFormEvents';
import { useExtractContentSidFromJson } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/hooks/useExtractBodyFromJson';
import {
  IWhatsAppInfoPayload,
  TemplateKey,
} from '@/src/modules/CampaignsModule/interfaces/WhatsApp';

import {
  atomActionInfoWhatsApp,
  atomCurrentWhatsAppTemplate,
  atomInfoWhatsAppPayload,
  atomMessageWhatsApp,
} from '@/modules/CampaignsModule/atoms/actionWhatsAppAtom';
import {
  atomWhatsAppTemplates,
  atomWhatsAppTemplateSelected,
} from '@/modules/ContactsModule/atoms/WhatsApp';

export const useTemplateManagement = ({
  setValue,
}: {
  setValue: UseFormSetValue<IWhatsAppFormInfo>;
}) => {
  const { actionId } = useParams();
  const { setInfoOnEvent } = useWhatsAppFormEvents({ actionId: Number(actionId) });
  const inboxService = useInboxService();

  const [selectedTemplate, setSelectedTemplate] = useAtom(atomWhatsAppTemplateSelected);
  const [messageWhatsApp, setMessageWhatsApp] = useAtom(atomMessageWhatsApp);
  const [infoWhatsAppPayload, setInfoWhatsAppPayload] = useAtom(atomInfoWhatsAppPayload);
  const [whatsAppTemplates, setWhatsAppTemplates] = useAtom(atomWhatsAppTemplates);

  const setActionInfoWhatsApp = useSetAtom(atomActionInfoWhatsApp);
  const setCurrentWhatsAppTemplate = useSetAtom(atomCurrentWhatsAppTemplate);

  const getTemplates = useCallback(async () => {
    const templates = await inboxService.getWhatsAppTemplatesByAccount();
    setWhatsAppTemplates(templates);
  }, [inboxService, setWhatsAppTemplates]);

  const contentSid = useExtractContentSidFromJson(messageWhatsApp.original);

  const replaceCustomVariables = useCallback(
    ({ message, variables }: { message: string; variables: Record<string, string> }) => {
      const variableRegex = /\$\{\d+#[^}]+\}/g;
      let index = 1;

      return message.replace(variableRegex, (match) => {
        const twilioVar = `{{${index}}}`;
        variables[`variable${index}`] = match;
        index++;
        return twilioVar;
      });
    },
    []
  );

  const handleSelectedTemplate = useCallback(() => {
    if (selectedTemplate) {
      const templateType = Object.keys(selectedTemplate.types)[0] as TemplateKey;
      const templateData = selectedTemplate.types[templateType];

      const message = {
        to: '',
        from: '',
        ContentSid: selectedTemplate.sid, // TODO: modify to use content_sid
        friendly_name: selectedTemplate.friendly_name,
        language: selectedTemplate.language,
        variables: selectedTemplate.variables,
        ContentVariables: selectedTemplate.variables,
        types: {
          [templateType]: {
            ...templateData,
            body: replaceCustomVariables({
              message: templateData?.body || '',
              variables: selectedTemplate.variables,
            }),
          },
        },
      };

      setCurrentWhatsAppTemplate(message);

      const messageJSON = JSON.stringify(message);

      setValue('message', messageJSON);

      setMessageWhatsApp({
        ...messageWhatsApp,
        original: messageJSON,
      });

      const payload: IWhatsAppInfoPayload = { ...infoWhatsAppPayload, mensaje: messageJSON };
      setInfoOnEvent(payload);
      setInfoWhatsAppPayload(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTemplate,
    replaceCustomVariables,
    setValue,
    setCurrentWhatsAppTemplate,
    setMessageWhatsApp,
    setInfoWhatsAppPayload,
    setInfoOnEvent,
  ]);

  const getPreviousSelectedTemplate = useCallback(() => {
    if (whatsAppTemplates && messageWhatsApp.original && !selectedTemplate) {
      const templateFound = whatsAppTemplates.find((template: WhatsAppTemplate) => {
        const templateType = Object.keys(template.types)[0] as TemplateKey;
        const templateSid = template.sid;

        if (templateType) {
          return templateSid === contentSid;
        }

        return false;
      });

      if (templateFound) {
        setSelectedTemplate(templateFound);
        handleSelectedTemplate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    whatsAppTemplates,
    messageWhatsApp.original,
    contentSid,
    handleSelectedTemplate,
    setSelectedTemplate,
  ]);

  const handleDeleteTemplate = () => {
    const payload: IWhatsAppInfoPayload = { ...infoWhatsAppPayload, mensaje: '' };
    setInfoOnEvent(payload);
    setInfoWhatsAppPayload((prev) => ({ ...prev, mensaje: '' }));
    setActionInfoWhatsApp((prev) => ({ ...prev, mensaje: '' }));
    setMessageWhatsApp({ ...messageWhatsApp, original: '' });
    setSelectedTemplate(null);
    setCurrentWhatsAppTemplate({
      to: '',
      from: '',
      ContentSid: '',
      friendly_name: '',
      language: '',
      variables: {},
      types: {
        'twilio/text': {
          body: '',
        },
      },
    });
  };

  return {
    getPreviousSelectedTemplate,
    selectedTemplate,
    handleSelectedTemplate,
    handleDeleteTemplate,
    getTemplates,
  };
};
